import JSZip from "jszip";
import { useEffect, useRef } from "react";
import {
  Chart, LineController, BarController, ScatterController,
  PieController, DoughnutController, RadarController,
  CategoryScale, LinearScale, RadialLinearScale,
  PointElement, LineElement, BarElement, ArcElement,
  Title, Tooltip, Legend, Filler,
} from "chart.js";

Chart.register(
  LineController, BarController, ScatterController,
  PieController, DoughnutController, RadarController,
  CategoryScale, LinearScale, RadialLinearScale,
  PointElement, LineElement, BarElement, ArcElement,
  Title, Tooltip, Legend, Filler,
);

// ─── XML helpers ──────────────────────────────────────────────────────────────

function stripNS(xml) {
  // Strip element namespace prefixes: <c:chart> → <chart>, </c:chart> → </chart>
  xml = xml.replace(/<(\/?)[a-zA-Z][a-zA-Z0-9]*:([a-zA-Z])/g, "<$1$2");
  // Strip all xmlns declarations (prefixed or default, single or double quotes)
  xml = xml.replace(/\s+xmlns(?::[a-zA-Z0-9_-]+)?=(?:"[^"]*"|'[^']*')/g, "");
  // Strip attribute namespace prefixes: r:id="x" → id="x"
  xml = xml.replace(/\s[a-zA-Z][a-zA-Z0-9]*:([a-zA-Z_][a-zA-Z0-9_-]*)\s*=/g, " $1=");
  return xml;
}

const get    = (el, sel) => el?.querySelector(sel) ?? null;
const getAll = (el, sel) => el ? [...el.querySelectorAll(sel)] : [];
const txt    = (el, sel) => get(el, sel)?.textContent?.trim() ?? "";

// ─── Cell range resolution ────────────────────────────────────────────────────

function colToLetter(n) {
  let s = ""; n++;
  while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); }
  return s;
}
function letterToCol(s) {
  let n = 0;
  for (const ch of s.toUpperCase()) n = n * 26 + ch.charCodeAt(0) - 64;
  return n - 1;
}

// Convert Excel date serial to JS Date string (e.g. 46057 → "2026-02-28")
function excelDateToString(serial) {
  if (!serial || typeof serial !== "number" || serial < 1) return serial;
  const d = new Date(Math.round((serial - 25569) * 86400 * 1000));
  return d.toLocaleDateString();
}

// live: { values, sheet } — the report's evaluated cell values, keyed by address,
// and the sheet they belong to. A chart is parsed from the template file, so
// without these it can only ever plot the template's own numbers.
function resolveRef(ref, wb, live) {
  if (!ref) return [];
  const clean = ref.replace(/\$/g, "").trim();
  const bang = clean.indexOf("!");
  let sheetName = wb.SheetNames[0];
  let range = clean;
  if (bang !== -1) {
    sheetName = clean.slice(0, bang).replace(/^'|'$/g, "");
    range = clean.slice(bang + 1);
  }
  const ws = wb?.Sheets?.[sheetName];
  if (!ws) return [];

  const m = range.match(/^([A-Za-z]+)(\d+)(?::([A-Za-z]+)(\d+))?$/);
  if (!m) return [];
  const c1 = letterToCol(m[1]), r1 = parseInt(m[2]) - 1;
  const c2 = m[3] ? letterToCol(m[3]) : c1;
  const r2 = m[4] ? parseInt(m[4]) - 1 : r1;

  const vals = [];
  for (let r = r1; r <= r2; r++) {
    for (let c = c1; c <= c2; c++) {
      const addr = colToLetter(c) + (r + 1);
      if (live?.values && sheetName === live.sheet && live.values[addr] !== undefined) {
        // Evaluated values arrive as display text, formatted to the cell's number
        // format. A chart axis needs numbers, so anything that reads as one is
        // converted; genuine labels are left alone.
        const lv = live.values[addr];
        if (typeof lv === "string") {
          const n = Number(lv.replace(/,/g, "").trim());
          vals.push(lv.trim() !== "" && Number.isFinite(n) ? n : lv);
        } else {
          vals.push(lv);
        }
        continue;
      }
      const cell = ws[addr];
      vals.push(cell != null ? (cell.v ?? null) : null);
    }
  }
  return vals;
}

// ─── Path helpers ─────────────────────────────────────────────────────────────

function resolvePath(base, target) {
  if (target.startsWith("/")) return target.slice(1);
  const dir = base.substring(0, base.lastIndexOf("/") + 1);
  const parts = (dir + target).split("/");
  const out = [];
  for (const p of parts) {
    if (p === "..") out.pop();
    else if (p !== ".") out.push(p);
  }
  return out.join("/");
}

function relsPathFor(path) {
  const parts = path.split("/");
  const name = parts.pop();
  return [...parts, "_rels", name + ".rels"].join("/");
}

async function readXml(zip, path) {
  const str = await zip.file(path)?.async("string");
  if (!str) return null;
  return new DOMParser().parseFromString(stripNS(str), "text/xml");
}

// ─── Cached chart data readers ────────────────────────────────────────────────
// Excel always caches chart data in numCache/strCache; prefer these over live cell lookup.

function readNumCache(refEl) {
  const cache = get(refEl, "numCache");
  if (!cache) return null;
  const count = parseInt(get(cache, "ptCount")?.getAttribute("val") || "0");
  if (count === 0) return null; // empty cache — fall back to live ref
  const vals = new Array(count).fill(null);
  getAll(cache, "pt").forEach(pt => {
    const idx = parseInt(pt.getAttribute("idx") ?? "0");
    const v = get(pt, "v")?.textContent;
    if (idx < count && v != null && v !== "") vals[idx] = parseFloat(v);
  });
  // If every value is null the cache was written but not populated — fall back
  return vals.some(v => v !== null) ? vals : null;
}

function readStrCache(refEl) {
  const cache = get(refEl, "strCache");
  if (!cache) return null;
  const count = parseInt(get(cache, "ptCount")?.getAttribute("val") || "0");
  if (count === 0) return null;
  const vals = new Array(count).fill("");
  getAll(cache, "pt").forEach(pt => {
    const idx = parseInt(pt.getAttribute("idx") ?? "0");
    const v = get(pt, "v")?.textContent;
    if (idx < count && v != null) vals[idx] = v;
  });
  return vals.some(v => v !== "") ? vals : null;
}

// ─── Chart XML parser ─────────────────────────────────────────────────────────

const PALETTE = [
  "#6366f1", "#f59e0b", "#10b981", "#ef4444",
  "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6",
  "#f97316", "#64748b",
];

const CHART_TYPE_MAP = {
  lineChart:     "line",
  barChart:      "bar",
  scatterChart:  "scatter",
  pieChart:      "pie",
  doughnutChart: "doughnut",
  areaChart:     "line",
  radarChart:    "radar",
  bubbleChart:   "bubble",
};

// numCache is what Excel last calculated for the TEMPLATE. Once a report supplies
// live values they are the truth and the cache is stale by definition, so it is
// used only when the live lookup came back with nothing.
function pickVals(cached, liveVals) {
  const hasLive = Array.isArray(liveVals)
    && liveVals.some(v => v !== null && v !== undefined && v !== "");
  if (hasLive) return liveVals;
  return cached ?? liveVals ?? [];
}

function parseChartXml(xmlStr, wb, live) {
  const doc = new DOMParser().parseFromString(stripNS(xmlStr), "text/xml");

  // Chart title
  let title = "";
  const titleEl = get(doc, "chart > title");
  if (titleEl) {
    const fEl = get(titleEl, "tx strRef f");
    if (fEl) {
      const vals = resolveRef(fEl.textContent, wb, live);
      title = vals[0] != null ? String(vals[0]) : "";
    } else {
      title = getAll(titleEl, "t").map(t => t.textContent).join("") || "";
    }
  }

  // Chart type
  const plotArea = get(doc, "plotArea");
  let chartType = "line", xmlChartTag = "lineChart";
  let isHorizontalBar = false, isFilled = false;

  for (const [tag, jsType] of Object.entries(CHART_TYPE_MAP)) {
    if (get(plotArea, tag)) {
      xmlChartTag = tag; chartType = jsType;
      if (tag === "barChart") {
        isHorizontalBar = get(plotArea, "barDir")?.getAttribute("val") === "bar";
      }
      if (tag === "areaChart") isFilled = true;
      break;
    }
  }

  // Series
  const chartEl = get(plotArea, xmlChartTag) || plotArea;
  const datasets = [];
  const globalLabels = [];
  let catsSet = false;

  getAll(chartEl, "ser").forEach((ser, i) => {
    // Series name — try cache first, then live ref, then inline text
    let serName = `Series ${i + 1}`;
    const txStrRef = get(ser, "tx strRef");
    const txNumRef = get(ser, "tx numRef");
    const txV = txt(ser, "tx v");
    if (txStrRef) {
      const cached = readStrCache(txStrRef);
      const v = cached?.[0] ?? resolveRef(txt(txStrRef, "f"), wb, live)[0];
      if (v != null && v !== "") serName = String(v);
    } else if (txNumRef) {
      const cached = readNumCache(txNumRef);
      const v = cached?.[0] ?? resolveRef(txt(txNumRef, "f"), wb, live)[0];
      if (v != null) serName = String(v);
    } else if (txV) {
      serName = txV;
    }

    // Color from spPr
    let color = PALETTE[i % PALETTE.length];
    const clrVal = get(ser, "spPr solidFill srgbClr")?.getAttribute("val");
    if (clrVal && /^[0-9A-Fa-f]{6}$/.test(clrVal)) color = "#" + clrVal;

    // Whether this series draws a connecting line, read from the workbook rather
    // than assumed. A scatter series carries <a:ln> under its own spPr; <a:noFill/>
    // inside it means the author turned the line off. :scope keeps this off the
    // marker's own spPr, which has an <a:ln> of its own.
    const lnEl    = get(ser, ":scope > spPr > ln");
    const lineOff = !!(lnEl && get(lnEl, ":scope > noFill"));
    const smoothV = get(ser, ":scope > smooth")?.getAttribute("val");
    const isSmooth = smoothV === "1" || smoothV === "true";
    // Conservative: only draw a line when the file actually asks for one, so
    // existing marker-only scatter charts keep rendering as they do today.
    const wantsLine = (!!lnEl && !lineOff) || isSmooth;

    // Categories — try strCache, then numCache, then live ref
    if (!catsSet) {
      const catStrRef = get(ser, "cat strRef");
      const catNumRef = get(ser, "cat numRef");
      let cats = null;
      if (catStrRef) {
        cats = pickVals(readStrCache(catStrRef), resolveRef(txt(catStrRef, "f"), wb, live)).map(v => v != null ? String(v) : "");
      } else if (catNumRef) {
        const raw = pickVals(readNumCache(catNumRef), resolveRef(txt(catNumRef, "f"), wb, live));
        // Convert date serials to readable strings; otherwise stringify
        cats = raw.map(v => v != null ? (typeof v === "number" && v > 40000 ? excelDateToString(v) : String(v)) : "");
      }
      if (cats?.length) { cats.forEach(v => globalLabels.push(v)); catsSet = true; }
    }

    // Scatter X — numCache first, then live ref
    const xValRef = get(ser, "xVal numRef");
    const xRaw = xValRef
      ? pickVals(readNumCache(xValRef), resolveRef(txt(xValRef, "f"), wb, live))
      : null;

    // Y / value — numCache first, then live ref
    const valNumRef = get(ser, "val numRef") || get(ser, "yVal numRef");
    const yRaw = valNumRef
      ? pickVals(readNumCache(valNumRef), resolveRef(txt(valNumRef, "f"), wb, live))
      : [];

    let data;
    if (chartType === "scatter" && xRaw) {
      data = xRaw
        .map((x, j) => ({ x, y: yRaw[j] }))
        .filter(pt => pt.x != null && pt.y != null);
      // If X values are Excel date serials, store as-is (converted to line chart below)
    } else if (chartType === "bubble") {
      const bubbleRef = get(ser, "bubbleSize numRef");
      const bRaw = bubbleRef ? pickVals(readNumCache(bubbleRef), resolveRef(txt(bubbleRef, "f"), wb, live)) : [];
      data = (xRaw || [])
        .map((x, j) => ({ x: x ?? 0, y: yRaw[j] ?? 0, r: bRaw[j] ?? 5 }))
        .filter(pt => pt.x != null && pt.y != null);
    } else {
      data = yRaw;
    }

    const isPieLike = chartType === "pie" || chartType === "doughnut";
    datasets.push({
      label: serName,
      data,
      borderColor: isPieLike ? PALETTE : color,
      backgroundColor: isPieLike
        ? PALETTE.slice(0, (Array.isArray(data) ? data.length : 10)).map(c => c + "cc")
        : isFilled ? color + "44" : color + "22",
      fill: isFilled,
      tension: isSmooth ? 0.4 : 0.2,
      pointRadius: chartType === "scatter" ? 4 : 3,
      borderWidth: 2,
      showLine: chartType === "scatter" ? wantsLine : undefined,
    });
  });

  // Post-process: scatter charts with date-serial X values can't render on a linear scale.
  // Convert them to line charts with date strings as category labels.
  let finalType = chartType;
  if (chartType === "scatter" && datasets.length > 0) {
    const firstPt = datasets[0].data?.[0];
    if (firstPt && typeof firstPt.x === "number" && firstPt.x > 40000) {
      // Use X values from first series as labels
      datasets[0].data.forEach(pt => globalLabels.push(excelDateToString(pt.x)));
      // Flatten all series from {x,y} pairs to y-only arrays
      datasets.forEach(ds => {
        ds.data = (ds.data || []).map(pt => (pt && typeof pt === "object") ? pt.y : pt);
        ds.showLine = undefined;
        ds.pointRadius = 3;
      });
      finalType = "line";
    }
  }

  // Axis scaling. Only meaningful for a value axis, which is what a scatter chart
  // has on x; a category axis has no scaling in the file.
  const xLogBase = get(doc, "valAx scaling logBase")?.getAttribute("val")
    || get(doc, "catAx scaling logBase")?.getAttribute("val") || null;
  const xReversed = (get(doc, "valAx scaling orientation")?.getAttribute("val")
    || get(doc, "catAx scaling orientation")?.getAttribute("val")) === "maxMin";

  // Axis labels
  const catAxTitle = getAll(doc, "catAx title")
    .map(t => getAll(t, "t").map(x => x.textContent).join("")).filter(Boolean)[0] || "";
  const valAxTitle = getAll(doc, "valAx title")
    .map(t => getAll(t, "t").map(x => x.textContent).join("")).filter(Boolean)[0] || "";

  // Y axis scale — OOXML stores min/max as val attributes, not child text
  const valAx = get(doc, "valAx");
  const axMin = get(valAx, "scaling min")?.getAttribute("val");
  const axMax = get(valAx, "scaling max")?.getAttribute("val");

  const isPieLike = finalType === "pie" || finalType === "doughnut";
  const isScatter = finalType === "scatter";

  return {
    type: finalType,
    data: {
      labels: (isScatter || finalType === "bubble") ? undefined : globalLabels,
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      indexAxis: isHorizontalBar ? "y" : "x",
      plugins: {
        title: title
          ? { display: true, text: title, font: { size: 13, weight: "bold" }, color: "#111" }
          : { display: false },
        legend: { position: "top", labels: { font: { size: 11 } } },
      },
      scales: isPieLike ? {} : {
        x: {
          // Excel records a log axis as <logBase>, and a reversed one as
          // orientation="maxMin". A particle size distribution is read on a
          // reversed log scale; on a linear axis every sieve below the No. 8 is
          // crushed against the origin and the curve is unreadable.
          type: xLogBase ? "logarithmic" : undefined,
          reverse: xReversed || undefined,
          title: catAxTitle ? { display: true, text: catAxTitle } : { display: false },
          ticks: { maxTicksLimit: 12, font: { size: 10 } },
        },
        y: {
          title: valAxTitle ? { display: true, text: valAxTitle } : { display: false },
          min: axMin != null ? parseFloat(axMin) : undefined,
          max: axMax != null ? parseFloat(axMax) : undefined,
          ticks: { font: { size: 10 } },
        },
      },
    },
  };
}

// ─── Find chart XMLs embedded in a worksheet ──────────────────────────────────

async function getSheetFilePath(sheetName, zip, wb) {
  const wbRelsDoc = await readXml(zip, "xl/_rels/workbook.xml.rels");
  const wbDoc     = await readXml(zip, "xl/workbook.xml");
  if (!wbRelsDoc || !wbDoc) return null;

  const sheetEls = getAll(wbDoc, "sheet");
  const sheetEl  = sheetEls.find(el => el.getAttribute("name") === sheetName)
    || sheetEls[wb.SheetNames.indexOf(sheetName)];
  if (!sheetEl) return null;

  const rId    = sheetEl.getAttribute("id");
  const relEl  = getAll(wbRelsDoc, "Relationship").find(r => r.getAttribute("Id") === rId);
  if (!relEl) return null;

  const target = relEl.getAttribute("Target");
  return target.startsWith("/") ? target.slice(1) : "xl/" + target;
}

// Returns array of Chart.js configs for all charts embedded in a worksheet
export async function parseChartsForSheet(sheetName, rawBytes, wb, live) {
  if (!rawBytes || !wb) return [];
  try {
    const zip = await JSZip.loadAsync(
      rawBytes instanceof Uint8Array ? rawBytes : new Uint8Array(rawBytes)
    );

    const sheetPath = await getSheetFilePath(sheetName, zip, wb);
    if (!sheetPath) { console.warn(`[charts] No sheet path for "${sheetName}"`); return []; }

    // Worksheet → drawing
    const sheetRelsPath = relsPathFor(sheetPath);
    const sheetRelsDoc = await readXml(zip, sheetRelsPath);
    if (!sheetRelsDoc) { console.warn(`[charts] No rels file at ${sheetRelsPath}`); return []; }
    const drawingRel = getAll(sheetRelsDoc, "Relationship")
      .find(r => r.getAttribute("Type")?.includes("drawing"));
    if (!drawingRel) { console.warn(`[charts] No drawing rel in ${sheetRelsPath}`); return []; }

    const drawingPath = resolvePath(sheetPath, drawingRel.getAttribute("Target"));

    // Drawing → charts
    const drawingRelsDoc = await readXml(zip, relsPathFor(drawingPath));
    if (!drawingRelsDoc) { console.warn(`[charts] No drawing rels at ${relsPathFor(drawingPath)}`); return []; }
    const chartRels = getAll(drawingRelsDoc, "Relationship")
      .filter(r => r.getAttribute("Type")?.includes("chart"));
    console.log(`[charts] Sheet "${sheetName}": found ${chartRels.length} chart rel(s)`);

    const configs = [];
    for (const rel of chartRels) {
      const chartPath = resolvePath(drawingPath, rel.getAttribute("Target"));
      const chartXml  = await zip.file(chartPath)?.async("string");
      if (chartXml) configs.push(parseChartXml(chartXml, wb, live));
    }
    return configs;
  } catch (e) {
    console.warn(`parseChartsForSheet "${sheetName}":`, e);
    return [];
  }
}

// ─── React component ──────────────────────────────────────────────────────────

export function ExcelChartPanel({ config, title }) {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  useEffect(() => {
    if (!config || !canvasRef.current) return;
    if (chartRef.current) chartRef.current.destroy();
    const cfg = JSON.parse(JSON.stringify(config));
    cfg.options = cfg.options || {};
    cfg.options.responsive = true;
    cfg.options.maintainAspectRatio = false;
    // Show title inside the chart in white
    cfg.options.plugins = cfg.options.plugins || {};
    cfg.options.plugins.title = {
      display: !!title,
      text: title || "",
      color: "#ffffff",
      font: { size: 13, weight: "bold" },
      padding: { bottom: 8 },
    };
    cfg.options.plugins.legend = cfg.options.plugins.legend || {};
    cfg.options.plugins.legend.labels = { ...(cfg.options.plugins.legend.labels || {}), color: "#94a3b8" };
    cfg.options.scales = cfg.options.scales || {};
    ["x","y"].forEach(ax => {
      if (cfg.options.scales[ax]) {
        cfg.options.scales[ax].ticks = { ...(cfg.options.scales[ax].ticks || {}), color: "#94a3b8" };
        cfg.options.scales[ax].grid = { ...(cfg.options.scales[ax].grid || {}), color: "rgba(148,163,184,0.15)" };
      }
    });
    chartRef.current = new Chart(canvasRef.current, cfg);
    return () => { chartRef.current?.destroy(); chartRef.current = null; };
  }, [config, title]);

  if (!config) return null;

  return (
    <div style={{ background: "#111827", borderTop: "1px solid #1e293b", padding: "12px 0 20px", display: "flex", justifyContent: "center" }}>
      <div style={{ width: 540, height: 240 }}>
        <canvas ref={canvasRef} style={{ width: "100%", height: "100%" }} />
      </div>
    </div>
  );
}

// Renders a chart config to an offscreen canvas and returns a PNG data URL.
// Used for embedding charts in PDF exports.
export function chartConfigToDataUrl(config, title, width = 800, height = 350) {
  return new Promise(resolve => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const cfg = JSON.parse(JSON.stringify(config));
      cfg.options = cfg.options || {};
      cfg.options.responsive = false;
      cfg.options.animation = false;
      cfg.options.maintainAspectRatio = false;
      cfg.options.plugins = cfg.options.plugins || {};
      cfg.options.plugins.title = { display: !!title, text: title || "", color: "#ffffff", font: { size: 13, weight: "bold" } };
      cfg.options.plugins.legend = { ...(cfg.options.plugins.legend || {}), labels: { color: "#cccccc" } };
      // Dark background for the canvas
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#111827";
      ctx.fillRect(0, 0, width, height);
      const chart = new Chart(canvas, cfg);
      setTimeout(() => {
        resolve(canvas.toDataURL("image/png"));
        chart.destroy();
      }, 200);
    } catch (e) {
      resolve(null);
    }
  });
}
